// Pure helpers for GRN-driven auto-fill on the Add Asset form.
// No Angular/service dependencies — keep this module side-effect free and unit-reviewable.

export interface GrnLineItem {
  item_id?: number | string;
  item_type?: number;
  rate?: number;
  [k: string]: any;
}

export interface GrnRecord {
  receipt_no?: string;
  grn_no?: string;
  vendor?: string;
  vendor_id?: number | string;
  po_number?: string;
  purchase_order_no?: string;
  pi_number?: string;
  created_date?: string;
  subsidiary_id?: number | string;
  location?: number | string;
  grn_items?: GrnLineItem[];
  [k: string]: any;
}

export interface GrnProcurementFill {
  procurement_vendor_name?: string;
  purchase_order_no?: string;
  pi_no?: string;
  purchase_date?: string;
}

// Only asset line items participate in rate matching.
export const ASSET_ITEM_TYPE = 2;

/**
 * Map a GRN record to the procurement display fields it can fill.
 * Omits empty/absent values so callers never patch blanks over real data.
 */
export function buildGrnProcurementFill(grn: GrnRecord): GrnProcurementFill {
  const fill: GrnProcurementFill = {};
  const vendor = grn.vendor;
  const po = grn.po_number ?? grn.purchase_order_no;
  const pi = grn.pi_number;
  const date = grn.created_date;
  if (vendor) fill.procurement_vendor_name = vendor;
  if (po) fill.purchase_order_no = po;
  if (pi) fill.pi_no = pi;
  if (date) fill.purchase_date = date;
  return fill;
}

/**
 * Best-effort per-unit rate for purchase_bill_value (spec §4).
 * 1) If selectedItemId matches exactly one asset line (by item_id) with a numeric rate, use it.
 * 2) Else if exactly one asset line (with a numeric rate) exists on the GRN, use it.
 * 3) Else return null — leave the field blank; never guess across unmatched lines.
 */
export function resolveGrnUnitRate(
  grnItems: GrnLineItem[] | undefined,
  selectedItemId: string | number | null | undefined
): number | null {
  const lines = (grnItems || []).filter(
    i => !!i && i.item_type === ASSET_ITEM_TYPE && typeof i.rate === 'number'
  );
  if (lines.length === 0) return null;

  if (selectedItemId !== null && selectedItemId !== undefined && selectedItemId !== '') {
    const matched = lines.filter(i => String(i.item_id) === String(selectedItemId));
    if (matched.length === 1) return matched[0].rate as number;
    if (matched.length > 1) return null; // ambiguous — do not guess
  }

  if (lines.length === 1) return lines[0].rate as number;
  return null;
}
